import { useRef, useState, type FormEvent } from 'react';
import { Link } from 'react-router';
import { api, errorMessage } from '../../shared/api/client';
import { Alert, Button, Card, EmptyState, ErrorState, Field, Loading } from '../../shared/ui/components';
import { useAsync } from '../../shared/ui/useAsync';
import { useMe } from '../session/SessionProvider';
import { formatDay, parseMoney } from './money';
import { CampaignProgress, EndDateField, FREQUENCY, money, PeriodChecks, PledgeSummary, type Campaign, type Pledge, type PledgePeriod } from './pledges-shared';

type Mine = { today: string; campaigns: (Campaign & { canJoin: boolean })[]; pledges: Pledge[] };

/** Promesas del miembro: sus promesas con cada entrega, enviar comprobantes e inscribirse a propósitos públicos. */
export function MyPledgesPage() {
  const me = useMe();
  const churchId = me.activeChurchId;
  const base = `/finance/churches/${churchId}`;
  const data = useAsync((signal) => (churchId ? api.get<Mine>(`${base}/me/pledges`, signal) : Promise.reject(new Error('Sin contexto'))), [base]);
  const [notice, setNotice] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [joining, setJoining] = useState<string | null>(null);
  const [sending, setSending] = useState<{ pledge: Pledge; period: PledgePeriod } | null>(null);

  if (!churchId) return <EmptyState title="Elige primero la iglesia en la que actúas." />;
  const run = async (action: () => Promise<unknown>, ok: string) => {
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      await action();
      setNotice(ok);
      data.reload();
      return true;
    } catch (e) {
      setError(errorMessage(e));
      return false;
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="page">
      <header className="page__header">
        <h1>Promesas</h1>
        <p className="muted">Lo que prometiste dar para la obra y cada entrega que llevas.</p>
        {me.can('finance.read') ? (
          <Link className="btn btn--secondary" to="/app/finanzas?ver=promesas">
            Administrar en Finanzas
          </Link>
        ) : null}
      </header>
      {notice ? <Alert tone="success">{notice}</Alert> : null}
      {error ? <Alert>{error}</Alert> : null}
      {data.status === 'loading' ? <Loading /> : null}
      {data.status === 'error' ? <ErrorState error={data.error} onRetry={data.reload} /> : null}
      {data.status === 'ready' ? (
        <div className="stack">
          {data.data.pledges.length === 0 ? <EmptyState title="Aún no tienes promesas." /> : null}
          {data.data.pledges.map((p) => {
            const c = data.data.campaigns.find((x) => x.id === p.campaignId);
            return (
              <Card key={p.id} title={c?.name ?? 'Promesa'}>
                <PledgeSummary p={p} />
                {p.statusNote ? <p className="small muted">{p.statusNote}</p> : null}
                <p className="small muted">Toca una fecha pendiente para enviar tu captura o comprobante.</p>
                <PeriodChecks
                  periods={p.periods}
                  onPick={p.status === 'cancelada' ? undefined : (x) => (x.status === 'falta' || x.status === 'rechazado' || x.status === 'proximo' ? setSending({ pledge: p, period: x }) : undefined)}
                  label={(x) => (x.status === 'falta' || x.status === 'rechazado' || x.status === 'proximo' ? 'Enviar comprobante' : '')}
                />
                {p.periods.filter((x) => x.status === 'rechazado' && x.reviewNote).map((x) => (
                  <p key={x.dueOn} className="small text-danger">Entrega del {formatDay(x.dueOn)}: {x.reviewNote}. Envía otro comprobante.</p>
                ))}
                {sending?.pledge.id === p.id ? <ReceiptForm base={base} pledge={p} period={sending.period} busy={busy} run={run} onDone={() => setSending(null)} /> : null}
                {c?.transferInfo ? <p className="small pledge-transfer"><strong>Para transferir:</strong> {c.transferInfo}</p> : null}
                {p.status === 'activa' && p.paidCount === 0 ? (
                  <Button variant="ghost" busy={busy} onClick={() => window.confirm('¿Cancelar tu promesa?') && void run(() => api.post(`${base}/me/pledges/${p.id}/cancel`), 'Promesa cancelada.')}>
                    Cancelar mi promesa
                  </Button>
                ) : null}
              </Card>
            );
          })}

          {data.data.campaigns.filter((c) => c.canJoin).length ? <h2>Propósitos abiertos</h2> : null}
          {data.data.campaigns.filter((c) => c.canJoin).map((c) => (
            <Card key={c.id} title={c.name}>
              {c.description ? <p>{c.description}</p> : null}
              <CampaignProgress c={c} />
              {c.transferInfo ? <p className="small pledge-transfer"><strong>Para transferir:</strong> {c.transferInfo}</p> : null}
              {joining === c.id ? (
                <JoinForm base={base} c={c} today={data.data.today} busy={busy} run={run} onDone={() => setJoining(null)} />
              ) : (
                <Button onClick={() => setJoining(c.id)}>Hacer una promesa</Button>
              )}
            </Card>
          ))}
        </div>
      ) : null}
    </div>
  );
}

type Run = (action: () => Promise<unknown>, ok: string) => Promise<boolean>;

function JoinForm({ base, c, today, busy, run, onDone }: { base: string; c: Campaign; today: string; busy: boolean; run: Run; onDone: () => void }) {
  const [error, setError] = useState<string | null>(null);
  function submit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const f = new FormData(e.currentTarget);
    const amountCents = parseMoney(String(f.get('amount') ?? ''));
    if (amountCents === null) return setError('Escribe una cantidad válida, por ejemplo 500.');
    setError(null);
    void run(() => api.post(`${base}/me/pledges`, {
      campaignId: c.id, amountCents, frequency: String(f.get('frequency')), startsOn: String(f.get('startsOn')), endsOn: String(f.get('endsOn') ?? '') || null, note: null,
    }), '¡Gracias! Tu promesa quedó registrada.').then((ok) => ok && onDone());
  }
  return (
    <form className="form pledge-form" onSubmit={submit} noValidate>
      <div className="form__row">
        <Field label="Cantidad" name="amount" inputMode="decimal" required placeholder="500" />
        <div className="field">
          <label htmlFor={`j-freq-${c.id}`}>Cada cuándo</label>
          <select id={`j-freq-${c.id}`} name="frequency" defaultValue="semanal">
            {Object.entries(FREQUENCY).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
          </select>
        </div>
      </div>
      <div className="form__row">
        <Field label="A partir de" name="startsOn" type="date" required defaultValue={today > c.startsOn ? today : c.startsOn} />
        <EndDateField id={`j-end-${c.id}`} defaultValue={c.endsOn} />
      </div>
      <p className="small muted">Solo Finanzas y la pastoral ven cuánto prometes.</p>
      {error ? <Alert>{error}</Alert> : null}
      <div className="actions">
        <Button type="submit" busy={busy}>Prometer</Button>
        <Button variant="ghost" onClick={onDone}>Cancelar</Button>
      </div>
    </form>
  );
}

function ReceiptForm({ base, pledge, period, busy, run, onDone }: { base: string; pledge: Pledge; period: PledgePeriod; busy: boolean; run: Run; onDone: () => void }) {
  const input = useRef<HTMLInputElement>(null);
  const [error, setError] = useState<string | null>(null);
  function submit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const file = input.current?.files?.[0];
    if (!file) return setError('Elige la captura o el PDF del comprobante.');
    const amountCents = parseMoney(String(new FormData(e.currentTarget).get('amount') ?? ''));
    if (amountCents === null) return setError('Escribe una cantidad válida.');
    setError(null);
    void run(() => api.upload(`${base}/me/pledges/${pledge.id}/receipts?dueOn=${period.dueOn}&amountCents=${amountCents}`, file), 'Comprobante enviado. Finanzas lo revisará y te avisará.').then((ok) => ok && onDone());
  }
  return (
    <form className="form pledge-form" onSubmit={submit} noValidate>
      <p className="small"><strong>Entrega del {formatDay(period.dueOn)}</strong></p>
      <Field label="Cantidad que diste" name="amount" inputMode="decimal" defaultValue={String(pledge.amountCents / 100)} />
      <div className="field">
        <label htmlFor={`rc-${pledge.id}`}>Captura o comprobante (imagen o PDF)</label>
        <input id={`rc-${pledge.id}`} ref={input} type="file" accept="application/pdf,image/jpeg,image/png,image/webp,image/gif" />
      </div>
      {error ? <Alert>{error}</Alert> : null}
      <div className="actions">
        <Button type="submit" busy={busy}>Enviar comprobante</Button>
        <Button variant="ghost" onClick={onDone}>Cancelar</Button>
      </div>
      <p className="small muted">Total prometido: {money(pledge.amountCents)} {FREQUENCY[pledge.frequency].toLowerCase()}.</p>
    </form>
  );
}
